import { defineStore } from "pinia";
import { ref } from "vue";

export type NotificationType = "success" | "error" | "info" | "warning";
export type NotificationKind = "default" | "reward";

export interface RewardNotificationPayload {
  questId: string;
  questName: string;
  amount: number;
}

/** Кнопка действия под текстом тоста: «Перейти в корзину» и подобное. */
export interface NotificationAction {
  label: string;
  handler: () => void;
}

export interface NotificationItem {
  id: number;
  message: string;
  type: NotificationType;
  kind: NotificationKind;
  displayTime: number;
  paused: boolean;
  remaining: number;
  timerId: ReturnType<typeof setTimeout> | null;
  startedAt: number;
  /**
   * Тост не исчезает сам — только крестиком или действием. Для ошибок, которые
   * требуют реакции (заказ не оформлен): пять секунд на прочтение причины и
   * решение — мало, а пропущенный тост оставляет человека без объяснения (CAB-782).
   */
  sticky: boolean;
  action?: NotificationAction;
  reward?: RewardNotificationPayload;
  onClick?: () => void;
}

const DEFAULT_DISPLAY_TIME = 5000;
const MAX_NOTIFICATIONS = 5;
const MAX_REWARD_NOTIFICATIONS = 3;

let nextId = 0;

export const useNotifications = defineStore("notifications", () => {
  const notifications = ref<NotificationItem[]>([]);

  // Legacy reactive state — kept for backward compatibility with storeToRefs consumers.
  // These are updated on each notify() so existing DxToast bindings keep working
  // until local DxToast instances are fully removed.
  const isToastVisible = ref(false);
  const toastMessage = ref("");
  const toastType = ref<NotificationType>("info");

  /**
   * Показ уведомления. Повтор того же текста и типа не создаёт второй тост —
   * продлевает уже висящий: параллельные запросы к одному эндпоинту (или
   * несколько компонентов, грузящих одни данные) раньше выкладывали стопку
   * одинаковых красных плашек, из которой ничего нельзя было понять.
   */
  function notify(message: string, type: NotificationType = "info", displayTime = DEFAULT_DISPLAY_TIME) {
    // Отбрасываем пустое сообщение раньше всего: призрачный тост без текста не должен появляться
    // ни от какого вызова (бэкенд иногда отдаёт `message: ""`). Проверка обязана быть первой строкой,
    // иначе счётчик `nextId` и legacy-состояние разъедутся с фактическим стеком.
    // String() — потому что часть вызывающих берёт текст из `any`-типизированного ответа бэкенда,
    // и trim() по не-строке уронил бы обработчик там, где раньше был лишь кривой тост.
    if (!String(message ?? "").trim()) return;

    const duplicate = notifications.value.find((n) => n.message === message && n.type === type);
    if (duplicate) {
      restartTimer(duplicate, displayTime);
      return;
    }

    const id = ++nextId;

    pushDefault({
      id,
      message,
      type,
      kind: "default",
      displayTime,
      paused: false,
      remaining: displayTime,
      timerId: null,
      startedAt: Date.now(),
      sticky: false,
    });
  }

  /**
   * Постоянный тост (CAB-782): висит, пока его не закроют крестиком или не
   * нажмут действие. Повтор того же текста второй плашки не создаёт — она уже
   * на экране. `action` — кнопка под текстом; после нажатия тост закрывается.
   */
  function notifySticky(message: string, type: NotificationType = "error", action?: NotificationAction) {
    if (!String(message ?? "").trim()) return;

    if (notifications.value.some((n) => n.message === message && n.type === type)) return;

    pushDefault({
      id: ++nextId,
      message,
      type,
      kind: "default",
      displayTime: 0,
      paused: false,
      remaining: 0,
      timerId: null,
      startedAt: Date.now(),
      sticky: true,
      action,
    });
  }

  /**
   * Общий хвост показа обычного тоста: таймер, стек с лимитом, legacy-состояние.
   * Из стека первым уходит самый старый исчезающий тост — постоянный вытесняется,
   * только когда других не осталось: иначе пять уведомлений подряд молча сняли бы
   * с экрана ошибку, которая ждала реакции.
   */
  function pushDefault(item: NotificationItem) {
    startTimer(item);
    notifications.value.push(item);

    const defaultItems = notifications.value.filter((n) => n.kind === "default");
    if (defaultItems.length > MAX_NOTIFICATIONS) {
      const evictable = defaultItems.find((n) => !n.sticky) ?? defaultItems[0];
      removeNotification(evictable.id);
    }

    // Legacy sync
    toastMessage.value = item.message;
    toastType.value = item.type;
    isToastVisible.value = true;
  }

  /**
   * Наградный тост о начислении токенов за квест онбординга (ТЕСТ ОНБОРДИНГ 2,
   * «Добавить наградные уведомления»): отдельный от обычных тостов стек (не
   * более 3 одновременно, лимит независим от общего MAX_NOTIFICATIONS),
   * отдельный контейнер в правом верхнем углу — см. AppNotifications kind
   * prop. `onClick` — открыть панель квестов и подсветить нужный пункт.
   */
  function notifyReward(reward: RewardNotificationPayload, onClick?: () => void) {
    const id = ++nextId;

    const item: NotificationItem = {
      id,
      message: "",
      type: "success",
      kind: "reward",
      displayTime: DEFAULT_DISPLAY_TIME,
      paused: false,
      remaining: DEFAULT_DISPLAY_TIME,
      timerId: null,
      startedAt: Date.now(),
      sticky: false,
      reward,
      onClick,
    };

    startTimer(item);
    notifications.value.push(item);

    const rewardItems = notifications.value.filter((n) => n.kind === "reward");
    if (rewardItems.length > MAX_REWARD_NOTIFICATIONS) {
      removeNotification(rewardItems[0].id);
    }
  }

  function startTimer(item: NotificationItem) {
    // постоянный тост таймера не имеет — ни при показе, ни после паузы
    if (item.sticky) return;
    item.startedAt = Date.now();
    item.timerId = setTimeout(() => {
      removeNotification(item.id);
    }, item.remaining);
  }

  /** Отсчёт показа с нуля — для повторного notify с тем же сообщением. */
  function restartTimer(item: NotificationItem, displayTime: number) {
    if (item.timerId) {
      clearTimeout(item.timerId);
      item.timerId = null;
    }
    item.displayTime = displayTime;
    item.remaining = displayTime;
    item.paused = false;
    startTimer(item);
  }

  function pauseNotification(id: number) {
    const item = notifications.value.find((n) => n.id === id);
    if (!item || item.paused) return;

    item.paused = true;
    if (item.timerId) {
      clearTimeout(item.timerId);
      item.timerId = null;
    }
    const elapsed = Date.now() - item.startedAt;
    item.remaining = Math.max(item.remaining - elapsed, 0);
  }

  function resumeNotification(id: number) {
    const item = notifications.value.find((n) => n.id === id);
    if (!item?.paused) return;

    item.paused = false;
    startTimer(item);
  }

  function removeNotification(id: number) {
    const idx = notifications.value.findIndex((n) => n.id === id);
    if (idx === -1) return;

    const item = notifications.value[idx];
    if (item.timerId) {
      clearTimeout(item.timerId);
    }
    notifications.value.splice(idx, 1);

    // Legacy sync
    if (notifications.value.length === 0) {
      isToastVisible.value = false;
    }
  }

  return {
    notifications,
    notify,
    notifySticky,
    notifyReward,
    pauseNotification,
    resumeNotification,
    removeNotification,
    // Legacy exports
    isToastVisible,
    toastMessage,
    toastType,
  };
});
